import {
  ValidTimeSlot,
  convertOccurrenceTimeframesToReadableForm,
  useListAvailableTimeslots,
} from '@carlito/bookito-client';
import { applyTimeOfDate, dateFromHHMM } from '@carlito/date-utils';
import { IonSpinner } from '@ionic/react';
import React, { Fragment, useEffect } from 'react';
import { Trans } from 'react-i18next';
import BookingTimeSlotList from '../../../BookingTimeSlotList/BookingTimeSlotList';
import { mapTimeFrameToTimeSlots } from '../../shared/helpers';
import { BookitoBookingFormProps, BookitoBookingFormValues } from '../../shared/types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useActiveLanguage } from '@carlito/api';

const EmptyTimeSlots = () => (
  <div className="text-center">
    <span className="text-md mt-8">
      <Trans>empty-time-slots</Trans>
    </span>
  </div>
);

type Props = {
  bookingParams: BookitoBookingFormValues;
  event: BookitoBookingFormProps['event'];
  ownerAuthId?: string;
  onSelectTimeSlot: ({ startDateTime, numOfConfirmedParticipants }: ValidTimeSlot) => void;
  maxParticipants: number | null | undefined;
};
export function BookingSlotPicker({
  bookingParams,
  ownerAuthId,
  maxParticipants,
  event,
  onSelectTimeSlot,
}: Props) {
  const { activeLanguage } = useActiveLanguage();
  const selectedDateTime = (bookingParams && bookingParams.bookedForDate) || new Date();
  const { isLoading, data, isError } = useListAvailableTimeslots({
    id: event.id,
    onDate: selectedDateTime?.toISOString(),
    authId: ownerAuthId,
  });

  const availableTimeSlots = data?.availableTimeSlots;
  const timeframes = convertOccurrenceTimeframesToReadableForm(event.occurrences);
  const timeFrameMappedSlots = mapTimeFrameToTimeSlots(timeframes, availableTimeSlots || []);

  useEffect(() => {
    if (availableTimeSlots?.length === 1) {
      const timeslot = availableTimeSlots[0];
      onSelectTimeSlot({
        startDateTime: timeslot.startDateTime,
        numOfConfirmedParticipants: timeslot.numOfConfirmedParticipants,
      });
    } else if (
      availableTimeSlots?.length === 0 &&
      event.timeBookability !== 'custom' &&
      event.maxParticipants
    ) {
      // Todo: clean this up when we implement proper timeslot wait list
      const startDateTime = applyTimeOfDate(
        selectedDateTime,
        new Date(event.occurrences[0].startDateTime),
        'Europe/Berlin'
      );
      if (startDateTime.getTime() > new Date().getTime()) {
        onSelectTimeSlot({
          startDateTime: startDateTime.toISOString(),
          numOfConfirmedParticipants: event.maxParticipants,
        });
      }
    }
  }, [availableTimeSlots]);

  const isSpaEvent = event.source === 'spa_system';

  return (
    <React.Fragment>
      <h2 className="text-lg font-bold mt-8 text-center">
        <Trans i18nKey="choose-your-time"></Trans>
      </h2>

      <div className="text-md font-semibold mt-4 text-center">
        {isLoading && <IonSpinner></IonSpinner>}
        {isError && <Trans i18nKey="something-went-wrong"></Trans>}
      </div>

      {!isLoading && !data?.availableTimeSlots.length && <EmptyTimeSlots />}

      {data?.availableTimeSlots &&
        data.availableTimeSlots.length > 0 &&
        timeFrameMappedSlots?.map(({ startTime, endTime, timeslots }) => {
          return (
            <div>
              {timeslots.length > 0 && (
                <Fragment>
                  {!isSpaEvent && (
                    <p className="text-md font-bold mt-8">
                      {startTime === endTime ? (
                        <Trans>all-day</Trans>
                      ) : (
                        `${dateFromHHMM(startTime).toLocaleTimeString(activeLanguage, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })} - ${dateFromHHMM(endTime).toLocaleTimeString(activeLanguage, {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      )}
                    </p>
                  )}
                  <BookingTimeSlotList
                    bookedForDate={
                      bookingParams.selectedTimeSlot?.startDateTime
                        ? new Date(bookingParams.selectedTimeSlot?.startDateTime)
                        : undefined
                    }
                    maxParticipants={maxParticipants}
                    onSelectTimeSlot={onSelectTimeSlot}
                    showMoreThreshold={6}
                    timeslotsData={data}
                    selectedEmployeeId={bookingParams.selectedTimeSlot?.employeeId}
                    duration={
                      event.timeBookabilitySettings?.timeBlockDurationMs
                        ? event.timeBookabilitySettings?.timeBlockDurationMs
                        : 0
                    }
                  />
                </Fragment>
              )}
            </div>
          );
        })}
    </React.Fragment>
  );
}
